import React, { useRef, useCallback, useEffect, useState } from 'react';
import CodeMirror from '../CodeMirror/CodeMirror';
import styles from './CodeEditor.scss';
import classnames from 'classnames/bind';
import { JSHINT } from 'jshint';
window.JSHINT = JSHINT;
/* eslint-disable no-multi-spaces */
const jshintOptions = {
    asi: true, // to forgive semicolons
    esversion: 9, // for ES9 (const, let, {...}, etc)
};
/* eslint-enable no-multi-spaces */
const cx = classnames.bind(styles);
const DEFAULT_OPTIONS = {
    autoRefresh: true,
    mode: 'javascript',
    lineNumbers: true,
    lint: jshintOptions,
    foldGutter: true,
    gutters: [
        'CodeMirror-linenumbers',
        'CodeMirror-foldgutter',
        'CodeMirror-lint-markers',
    ],
    styleActiveLine: true,
    styleSelectedText: true,
};
export const THEMES = {
    light: 'base16-light',
    dark: 'base16-dark',
};
export const LINE_STATUS = {
    LINE_RUNNING: 1,
    LINE_DONE: 2,
    LINE_ERROR: 3,
};
export const EDITOR_STATUS = {
    EDITOR_RUNNING: 1,
    EDITOR_DONE: 2,
    EDITOR_STOP: 3,
    EDITOR_PAUSED: 4,
    EDITOR_ERROR: 5,
};
const CodeEditor = ({ onScriptChange, onThemeToggle, onScriptRun, onScriptStop, script = '', theme = THEMES.light, line = { number: -1, status: 0 }, status = 0, readOnly = false, hintItems, }) => {
    const editorRef = useRef(null);
    const markedRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    // Mark editor as ready after initial mount to trigger line status check
    useEffect(() => {
        if (script) {
            setIsReady(true);
        }
    }, [script]);
    const checkLineStatus = useCallback(() => {
        const { LINE_RUNNING, LINE_ERROR, LINE_DONE } = LINE_STATUS;
        const { EDITOR_PAUSED, EDITOR_STOP, EDITOR_DONE } = EDITOR_STATUS;
        if (markedRef.current || (line.status === LINE_DONE && markedRef.current)) {
            markedRef.current?.clear();
        }
        if (editorRef.current &&
            editorRef.current.codeMirror &&
            status !== EDITOR_DONE) {
            if (line.status === 1) {
                editorRef.current.codeMirror.scrollIntoView(line.number - 1);
            }
            markedRef.current = editorRef.current.codeMirror.doc.markText({
                line: line.number - 1,
                ch: 0,
            }, {
                line: line.number,
                ch: 0,
            }, {
                className: cx({
                    styledBackground: line.status === LINE_RUNNING,
                    pausedBackground: status === EDITOR_PAUSED,
                    errorBackground: line.status === LINE_ERROR || status === EDITOR_STOP,
                }),
            });
        }
    }, [line, status]);
    // Check line status when line or status changes
    useEffect(() => {
        if (isReady) {
            checkLineStatus();
        }
    }, [isReady, line, status, checkLineStatus]);
    const handleKeyDown = useCallback((evt) => {
        if (evt.ctrlKey) {
            const keyCodes = {
                e: 69,
                i: 73,
                p: 80,
                s: 83,
            };
            switch (evt.keyCode) {
                case keyCodes.e:
                    onScriptRun(script);
                    evt.preventDefault();
                    break;
                case keyCodes.i:
                    onThemeToggle();
                    evt.preventDefault();
                    break;
                case keyCodes.s:
                    onScriptStop();
                    break;
            }
        }
    }, [onScriptRun, onThemeToggle, onScriptStop, script]);
    const options = {
        ...DEFAULT_OPTIONS,
        theme,
        readOnly,
    };
    return (React.createElement("div", { className: styles.container, onKeyDown: handleKeyDown },
        React.createElement(CodeMirror, { id: "editor", ref: editorRef, value: script, defaultValue: script, options: options, onChange: onScriptChange, hintItems: hintItems })));
};
export default CodeEditor;
